import * as React from 'react';
import { NavLink } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';

import { imageSize } from '@common/react/utils/utils';
import ResizeObserverContainer from '@common/react/components/UI/ResizeObserverContainer/ResizeObserverContainer';

import { Case, CasePage } from '@commonTuna/react/objects/BaseCase';
import ImageWithSkeleton from '@commonTuna/react/components/UI/ImageWithSkeleton/ImageWithSkeleton';

interface ComponentProps {
	item: Case;
	onCaseClick: (caseId: number) => void;
}

const serviceNode = (page) => {
	const path = page.page?.fullPath;
	const tag = (<div className="ant-tag" key={page.page.id} title={page.page.shortNameEn} style={{ fontWeight: 600 }}>
		{page.page.shortNameEn}
	</div>);
	return !path ? tag
		: (
			<NavLink
				key={page.id}
				to={`/${(path || '').replace(/^\//, '')}`}
			>
				{tag}
			</NavLink>
		);
};

const moreTagWidth = 42;

const Services: React.FC<{items: Array<CasePage>, caseId }> = ({ caseId, items }) => {
	const [count, setCount] = React.useState(0);
	const tagsWidth = React.useRef<any>();

	const id = `case${caseId}`;

	return <ResizeObserverContainer
		onResize={({ width, element }) => {
			if (!tagsWidth.current) {
				tagsWidth.current = [];
				let elementChild = element?.firstElementChild;
				while (elementChild) {
					tagsWidth.current.push(elementChild.getBoundingClientRect().width);
					elementChild = elementChild.nextElementSibling;
				}
			}

			if (tagsWidth.current) {
				let elementsWidth = tagsWidth.current.reduce((acc, width) => width + acc, 0) > width ? moreTagWidth : 0;
				let i;
				for (i = 0; i < tagsWidth.current.length; i++) {
					elementsWidth += tagsWidth.current[i];
					if (width < elementsWidth) break;
				}
				setCount(tagsWidth.current.length - i);
			}
		}}
		className={`page-list ${items.length ? 'page-list__long' : ''}`}
	>
		{items.slice(0, items.length - count).map((item) => serviceNode(item))}
		{count > 0 ? <>
			<button data-tooltip-id={id} type="button" className="inline-select-item inline-select-item_sm">
				+
				{count}
			</button>
			<Tooltip
				place="top"
				id={id}
				clickable
				positionStrategy="fixed"
			>
				{items.slice(items.length - count).map((item) => serviceNode(item))}
			</Tooltip>
		</> : null}
	</ResizeObserverContainer>;
};

const BeforeAfterItem = (props: ComponentProps) => {
	const { item, onCaseClick } = props;

	const name = item.nameEn;

	return <div className="ba-breast clearfix">
		<div
			className="ba-breast__title"
			onClick={(e) => onCaseClick(item.id)}
			title={name}
		>
			{`#${item.indexInCompany} `}
			{item.nameEn ? `${item.nameEn.slice(0, 35)}${item.nameEn.length > 35 ? '...' : ''}` : ''}
		</div>
		<div className="ba-breast__photos clearfix" onClick={(e) => onCaseClick(item.id)}>
			<div className="ba-breast__photos_left">
				<div>
					<ImageWithSkeleton
						withLazy
						src={imageSize(item.before, '_main')}
						alt={`${name} before`}
						loaderContainerClassName="ba-image__loader fa fa-file-image-o"
					/>
				</div>
			</div>
			<div className="ba-breast__photos_right">
				<div>
					<ImageWithSkeleton
						withLazy
						loaderContainerClassName="ba-image__loader fa fa-file-image-o"
						src={imageSize(item.after, '_main')}
						alt={`${name} after`}
					/>
				</div>
			</div>
		</div>
		{item.pages?.length ? <div className="ba-breast__body">
			<div className="row mb10">
				<div className="col-xs-12">
					<label className="param-label">
						{`Service${item?.pages?.length > 1 ? 's' : ''}:`}
					</label>
					<Services items={item.pages} caseId={item.id} />
				</div>
			</div>
		</div> : null}
	</div>;
};

export default BeforeAfterItem;
